import "./_style.less";
import { useTranslation } from "react-i18next";
import { useBookingAssessment } from "../../providers/booking-assessment.provider";
import { useGetAssessments } from "../../../assessment/hooks/use-get-assessments";
import { useGetAssessmentPsychologists } from "../../../assessment-psychologists/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Skeleton, Space } from "antd";
import PackageCardList from "../package-card-list";
import { VuiContentLoading, VuiFormCard, VuiSearch } from "../../../../@vendor/components";
import { useDebounce } from "usehooks-ts";
import PsychologistCard from "../../../psychologist/components/psychologist-card";
import { AssessmentPackageDetailModal, NoContentCard } from "../../../../components";
import { useStepTransition } from "../../hooks/use-step-transition";

const BookingAssessmentStepPackage = () => {
  const psychologistSectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { currentStep, form, onNextStep } = useBookingAssessment();
  const { values, setFieldValue, resetForm } = form;
  const { handleAssessmentChange, handlePsychologistChange } = useStepTransition({
    values,
    setFieldValue,
    resetForm,
  });

  const [selectedAssessmentDetail, setSelectedAssessmentDetail] = useState<any>(null);

  const {
    data: psychologists,
    loadData: loadPsychologistData,
    isLoading: isLoadingPsychologist,
  } = useGetAssessmentPsychologists(values?.assessment_id ?? "");

  const {
    data: assessments,
    loadData: loadAssessmentData,
    isLoading: isLoadingAssessment,
  } = useGetAssessments();

  const [search, setSearch] = useState<string>("");
  const debounceSearch = useDebounce(search, 300);
  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }

  useMemo(() => {
    loadAssessmentData().then()
  }, [loadAssessmentData]);

  useEffect(() => {
    if (!values?.assessment_id) return;

    loadPsychologistData().then()
  }, [values?.assessment_id, loadPsychologistData]);

  const filteredPsychologists = useMemo(() => {
    if (!debounceSearch) return psychologists;
    return psychologists.filter((item) =>
      item.name && item.name.toLowerCase().includes(debounceSearch.toLowerCase())
    );
  }, [psychologists, debounceSearch]);

  const assessmentHasSchedule = values?.assessment?.has_schedule;

  const handlePackageSelect = (id?: number) => {
    if (typeof id === "undefined") {
      handleAssessmentChange(null as any, null);
      return;
    }

    const selectedAssessment = assessments?.find((a) => a.id === id) ?? null;
    handleAssessmentChange(id, selectedAssessment);

    if (!selectedAssessment?.has_schedule) {
      onNextStep();
    }
  };

  useEffect(() => {
    if (assessmentHasSchedule && values.assessment?.has_schedule) {
      psychologistSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [values.assessment]);

  const handleOpenPackageDetail = (assessment: any) => {
    setSelectedAssessmentDetail(assessment);
  };

  const handleClosePackageDetail = () => {
    setSelectedAssessmentDetail(null);
  };

  useEffect(() => {
    if (currentStep === 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep]);

  if (currentStep !== 0) {
    return null;
  }

  return (
    <>
      <AssessmentPackageDetailModal
        visible={!!selectedAssessmentDetail}
        onClose={handleClosePackageDetail}
        assessment={selectedAssessmentDetail || {}}
      />

      <Row gutter={[20, 48]}>
        <Col xs={24}>
          <div className="form-group">
            <div className="choose-package-label">
              {t("common.text.choosePackage")}
            </div>
            {isLoadingAssessment ? (
              <VuiContentLoading loading={isLoadingAssessment} />
            ) : (
              <PackageCardList
                value={Number(values.assessment_id)}
                assessments={assessments}
                onClick={handlePackageSelect}
                onViewDetail={(assessment) => handleOpenPackageDetail(assessment)}
              />
            )}
          </div>
        </Col>

        {assessmentHasSchedule && (
          <Col xs={24}>
            <div className="form-group" ref={psychologistSectionRef}>
              <Space
                direction="vertical"
                size={20}
                style={{ width: "100%" }}
              >
                <Row gutter={[20, 20]} justify="space-between" align="middle">
                  <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                    <div className="choose-psychologist-label">
                      {t("common.text.chooseYourPsychologist")}
                    </div>
                  </Col>
                  <Col xs={{ span: 24 }} sm={{ span: 7 }}>
                    <VuiSearch
                      placeholder={t("common.text.selectPsychologist")}
                      value={search}
                      onChange={onSearchChange}
                    />
                  </Col>
                </Row>

                <Row gutter={[20, 20]}>
                  <Col sm={24}>
                    <Row gutter={[20, 20]}>
                      {isLoadingPsychologist ? (
                        <Col xs={24}>
                          <Row gutter={[20, 20]}>
                            {[...Array(2)].map((_, idx) => (
                              <Col xs={24} sm={12} key={idx}>
                                <VuiFormCard>
                                  <Skeleton avatar active paragraph={{ rows: 4 }} />
                                </VuiFormCard>
                              </Col>
                            ))}
                          </Row>
                        </Col>
                      ) : filteredPsychologists.length ? (
                        filteredPsychologists.map((item) => (
                          <Col xs={24} lg={12} key={`psychologist_${item.id}`}>
                            <PsychologistCard
                              data={item}
                              isSelected={values.psychologist_id === item.id}
                              onSelect={(p) => {
                                handlePsychologistChange(Number(p?.id), p);
                                onNextStep();
                              }}
                              nearestSchedule={item.nearest_counseling_schedule_date}
                            />
                          </Col>
                        ))
                      ) : (
                        <Col xs={24}>
                          <NoContentCard title={t("common.text.psychologist")} />
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Space>
            </div>
          </Col>
        )}

      </Row>
    </>
  );
};

export default BookingAssessmentStepPackage;
